import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subscription, BehaviorSubject, filter, of, combineLatest, map, shareReplay, tap, switchMap, take } from 'rxjs';
import { MessageService } from 'primeng/api';
import moment from 'moment-timezone';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { showZeroToTwoDecimal } from '../../Misc/helpers/formatting/number.helper';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { download } from '../../Misc/helpers/download/download.helper';
import { EventsOverTimeApiModel, EventsOverTimeQueryApiModel } from '../model/statisticsReport/events.over.time.api.model';
import { RecipientReportDataModel } from '../components/statisticReport/RecipientReport/recipient.report.data.model';
import { LinksReportModel } from '../components/statisticReport/PerformanceReport/links-report.model';
import { StatisticsApiInterface } from '../model/statisticsReport/statistics-api.model';
import { CurrentResourceInterface } from '../../Basic/models/current-resource-abstract.model';
import { DistributionAccountStatisticsModel } from '../model/statisticsReport/distribution-account-statistics.model';
import { AgprMath } from '@Common/Misc/helpers/math/math-helper';

@Injectable()
export abstract class StatisticsReportService implements OnDestroy {

    cachedResourceId: number;
    protected resourceIdSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    reportDataLoading = false;
    eventsOverTimeLoading = false;
    bodyLoading = false;

    protected resourceUuidSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    resourceUuid$ = this.resourceUuidSubject.asObservable();

    protected loadingSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    loading$ = this.loadingSubject.pipe(map(x => this.reportDataLoading || this.eventsOverTimeLoading || this.bodyLoading));

    recipientReportData$ = this.resourceIdSubject.pipe(
        switchMap(id => {
            if (id) {
                this.reportDataLoading = true;
                this.loadingSubject.next();
                return combineLatest([this.getResourceApi().getResourceReport(id), this.getAccountBenchmarks()]).pipe(
                    map(([reportApiModel, accountBenchmarks]) => {
                        this.reportDataLoading = false;
                        this.loadingSubject.next();
                        return new RecipientReportDataModel(reportApiModel, accountBenchmarks);
                    })
                );
            } else {
                this.reportDataLoading = false;
                this.loadingSubject.next();

                return of (null);
            }
        }),
        shareReplay(1)
    );

    private eventsOverTimeQuerySubject: BehaviorSubject<EventsOverTimeQueryApiModel> = new BehaviorSubject<EventsOverTimeQueryApiModel>(null);
    eventsOverTime$: Observable<EventsOverTimeApiModel> = combineLatest([this.resourceIdSubject, this.eventsOverTimeQuerySubject])
        .pipe(switchMap(([id, eventsOverTimeQuery]) => {
            if (!eventsOverTimeQuery || !id) {
                return of(null);
            }

            this.eventsOverTimeLoading = true;

            return this.getResourceApi().getEventsOverTime(id, eventsOverTimeQuery).pipe(
                tap(x => {
                    this.eventsOverTimeLoading = false;
                    this.loadingSubject.next();;
                }),
                shareReplay(1)
            );
        }));

    protected mppEstimatesEnabledSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    linksReport$ = combineLatest([this.mppEstimatesEnabledSubject, this.resourceIdSubject, this.recipientReportData$])
        .pipe(switchMap(([isMppEstimatesEnabled, id, recipientReportData]) => {
            if (!id) {
                return of(null);
            }

            return this.getResourceApi().getLinksReport(id).pipe(map(linkStats => {
                const res = new LinksReportModel(linkStats);
                this.calculateLinkReports(res, isMppEstimatesEnabled, recipientReportData);
                return res;
            }));
        }));

    resourceBody$ = this.resourceIdSubject.pipe(switchMap(id => {
        if (!id) {
            return of(null);
        }

        this.bodyLoading = true;

        return this.getResourceApi().getResourceBody(id).pipe(tap(x => {
            this.bodyLoading = false;
            this.loadingSubject.next();
        }));
    }));

    subscriptions: Subscription[] = [];

    topStatLabels = new Map<string, string>([
        ['sentCount', 'Discover.distributionReport.recipientCards.events.sent'],
        ['blocked', 'Discover.distributionReport.recipient.statsType.blocked'],
        ['hardBounced', 'Discover.distributionReport.recipient.statsType.hardBounced'],
        ['softBounced', 'Discover.distributionReport.recipient.statsType.softBounced'],
        ['reject', 'Discover.distributionReport.recipient.statsType.reject'],
        ['previousSoftBounced', 'Discover.distributionReport.recipient.statsType.previouslySoftBounced'],
        ['previousSpamReport', 'Discover.distributionReport.recipient.statsType.previouslySpamReported'],
        ['previousHardBounced', 'Discover.distributionReport.recipient.statsType.previouslyHardBounced'],
        ['unopened', 'Discover.distributionReport.recipient.statsType.unopened'],
        ['reliableUniqueOpens', 'Discover.distribution.statistics.reliableUniqueOpens'],
        ['reliableRecipients', 'Discover.distribution.statistics.reliableRecipients'],
        ['mppRecipients', 'Discover.distribution.statistics.mppRecipients'],
        ['deliveredCount', 'Discover.distributionReport.recipient.statsType.delivered'],
        ['deliveredRatio', 'Discover.distributionDashboard.statistics.deliveryRate'],
        ['uniqueOpenCount', 'Discover.distributionReport.recipient.statsType.uniqueOpens'],
        ['mppImpactedUniqueOpens', 'Discover.distribution.statistics.mppImpactedUniqueOpens'],
        ['estimatedActualUniqueOpens', 'Discover.distribution.statistics.actualUniqueOpens'],
        ['openCount', 'Discover.distributionReport.recipient.statsType.totalOpens'],
        ['reliableOpens', 'Discover.distribution.statistics.reliableOpens'],
        ['mppImpactedOpens', 'Discover.distribution.statistics.mppImpactedOpens'],
        ['estimatedActualOpens', 'Discover.distribution.statistics.actualOpens'],
        ['uniqueOpenRatio', 'Discover.distributionReport.recipient.statsType.openRate'],
        ['estimatedActualOpenRate', 'Discover.distributionReport.recipient.statsType.estimatedActualOpenRate'],
        ['uniqueClickCount', 'Discover.distributionReport.recipient.statsType.uniqueClicks'],
        ['clickCount', 'Discover.distributionReport.recipient.statsType.totalClicks'],
        ['uniqueClickRatio', 'Discover.distributionReport.recipient.statsType.clickRate'],
        ['clickThroughRate', 'Discover.distribution.statistics.clickThroughRate'],
        ['estimatedClickThroughRate', 'Discover.distribution.statistics.estimatedClickThroughRate'],
        ['unsubscribed', 'Discover.distributionReport.recipient.unsubscribes'],
        ['unsubscribeRate', 'Discover.distributionDashboard.statistics.unsubscribeRate'],
        ['spamReport', 'Discover.distributionDashboard.statistics.spamReportRate.label'],
        ['previousUnsubscribe', 'Discover.distributionReport.recipient.statsType.previousUnsubscribe'],
        ['bounceRate', 'Discover.distributionDashboard.statistics.bounceRate'],
    ]);

    private exportFields: string[] = [
        'sentCount',
        'deliveredCount',
        'deliveredRatio',
        'uniqueOpenCount',
        'reliableUniqueOpens',
        'mppImpactedUniqueOpens',
        'estimatedActualUniqueOpens',
        'openCount',
        'reliableOpens',
        'mppImpactedOpens',
        'estimatedActualOpens',
        'uniqueOpenRatio',
        'estimatedActualOpenRate',
        'uniqueClickCount',
        'clickCount',
        'uniqueClickRatio',
        'clickThroughRate',
        'estimatedClickThroughRate',
        'unopened',
        'unsubscribed',
        'unsubscribeRate',
        'spamReport',
        'previousUnsubscribe',
        'blocked',
        'softBounced',
        'hardBounced',
        'bounceRate'
    ];

    constructor(
        protected messageService: MessageService,
        protected localizationService: LocalizationService,
    ) {}

    abstract getResourceApi(): StatisticsApiInterface;
    abstract getResourceService(): CurrentResourceInterface;
    getAccountBenchmarks(): Observable<DistributionAccountStatisticsModel> {
        return of(null);
    };

    calculateLinkReports(linksReport: LinksReportModel, isMppEstimatesEnabled: boolean = false, recipientReportData: RecipientReportDataModel) {
        if (linksReport && recipientReportData) {
            if (isMppEstimatesEnabled) {
                linksReport.calculateClickThroughRates(recipientReportData.estimatedActualUniqueOpens);
                linksReport.totalClickThroughRate = recipientReportData.estimatedActualUniqueOpens ?
                    recipientReportData.uniqueClickCount / recipientReportData.estimatedActualUniqueOpens : 0;
            } else {
                linksReport.calculateClickThroughRates(recipientReportData.uniqueOpenCount);
                linksReport.totalClickThroughRate = recipientReportData.uniqueOpenCount ?
                    recipientReportData.uniqueClickCount / recipientReportData.uniqueOpenCount : 0;
            }
        }
    }

    setEventsOverTimeQuery(eventsOverTimeQuery: EventsOverTimeQueryApiModel) {
        this.eventsOverTimeQuerySubject.next(eventsOverTimeQuery);
    }

    setMppEstimates(isMppEstimatesEnabled: boolean = false): void {
        this.mppEstimatesEnabledSubject.next(isMppEstimatesEnabled);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    downloadReport(name: string): void {
        let exportText = '';

        this.recipientReportData$.pipe(take(1)).subscribe(recipientReportData => {

            for (const textField of this.exportFields) {
                switch (textField) {
                    case 'clickThroughRate':
                        const clickThroughRate = AgprMath.safeDivide(recipientReportData.uniqueClickCount, recipientReportData.uniqueOpenCount);

                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + showZeroToTwoDecimal(clickThroughRate) + '\n';
                        break;
                    case 'estimatedClickThroughRate':
                        const estimatedClickThroughRate =
                            AgprMath.safeDivide(recipientReportData.uniqueClickCount, recipientReportData.estimatedActualUniqueOpens);

                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + showZeroToTwoDecimal(estimatedClickThroughRate) + '\n';
                        break;
                    case 'reliableUniqueOpens':
                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + recipientReportData.reliableUniqueOpenCount + '\n';
                        break;
                    case 'mppImpactedUniqueOpens':
                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + recipientReportData.mppOpens + '\n';
                        break;
                    case 'mppImpactedOpens':
                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + recipientReportData.mppOpens + '\n';
                        break;
                    case 'uniqueOpenRatio':
                    case 'uniqueClickRatio':
                    case 'deliveredRatio':
                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + showZeroToTwoDecimal(recipientReportData[textField]) + '\n';
                        break;
                    case 'bounceRate':
                    case 'unsubscribeRate':
                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + showZeroToTwoDecimal(recipientReportData[textField] / 100) + '\n';
                        break;
                    case 'estimatedActualOpenRate':
                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + showZeroToTwoDecimal(
                            AgprMath.safeDivide(recipientReportData.estimatedActualUniqueOpens, recipientReportData.deliveredCount)
                        ) + '\n';
                        break;
                    default:
                        exportText += this.localizationService.transform(this.topStatLabels.get(textField)) +
                            '  ' + recipientReportData[textField] + '\n';
                        break;
                }
            }

            let filename = name;
            filename += '_Performance_';
            filename += moment().format('YYYYMMDD');
            filename += '.txt';

            const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8;'});

            download.blob(blob, filename);
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: this.localizationService.transform('Discover.distributionReport.export.successHeading'),
                detail: this.localizationService.transform('Discover.distributionReport.export.successMessage')
            });
        });

    }

    setupSubscription(): void {
        this.subscriptions.push(
            this.getResourceService().getFocusedResource().pipe(filter(x => !!x)).subscribe(resource => {
                if (this.cachedResourceId === resource.id) {
                    return;
                }

                this.cachedResourceId = resource.id;
                this.setResourceId(resource.id);
            })
        );
    }

    setResourceId(id: number): void {
        this.resourceIdSubject.next(id);
    }
}
